/**
 * Infra UI API Client
 *
 * BFF pattern: Authentication via HttpOnly cookie (agion_sso_session).
 * APISIX forward-auth plugin resolves session to JWT for upstream services.
 * All routes go through the gateway at /v1/infra/{resource}.
 */

import ky, { type KyInstance, HTTPError } from 'ky'
import { handleUnauthorized } from '@agion/auth'
import { keysToCamel, keysToSnake } from '@/core/infra_utils'
import { getConfig } from '@/core/infra_runtime_env'
import { ACCESS_DENIED_PATH, accessDeniedPath } from '@/core/infra_access_denied'

// ============= Configuration =============

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '')
const GATEWAY_URL = trimTrailingSlash(getConfig('VITE_API_URL', 'http://localhost:8090') || '/api')
const getObservabilityGatewayUrl = (): string => {
  const configured = getConfig(
    'VITE_OBSERVABILITY_API_URL',
    import.meta.env.DEV ? GATEWAY_URL : undefined
  )
  if (!configured) {
    throw new Error('CONFIGURATION ERROR: VITE_OBSERVABILITY_API_URL is required.')
  }
  return trimTrailingSlash(configured)
}
const API_TIMEOUT = 30000
const RETRY_ATTEMPTS = 3

// ============= Error Handling =============

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
    public code?: string
  ) {
    // HTTP/2 has no statusText — prefer the error message from the response body.
    // governance-service uses 'message' for human-readable text; infra-service uses 'error'.
    const body = typeof data === 'object' && data !== null
      ? data as Record<string, unknown>
      : undefined
    const bodyMessage = (typeof body?.['message'] === 'string' ? body['message'] : undefined)
      || (typeof body?.['error'] === 'string' ? body['error'] : undefined)
    const displayMessage = bodyMessage || statusText || `HTTP ${status}`
    super(`API Error ${status}: ${displayMessage}`)
    this.name = 'ApiError'
  }
}

// ============= HTTP Client =============

const httpClient: KyInstance = ky.create({
  prefix: `${GATEWAY_URL}/v1/infra`,
  // Preserve Ky 1's successful empty-body result for command endpoints.
  parseJson: text => text === '' ? '' : JSON.parse(text),
  timeout: API_TIMEOUT,
  credentials: 'include', // BFF Pattern: Send agion_sso_session cookie
  retry: {
    limit: RETRY_ATTEMPTS,
    methods: ['get'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      ({ request }) => {
        if (!request.headers.has('Content-Type')) {
          request.headers.set('Content-Type', 'application/json')
        }
        request.headers.set('Accept', 'application/json')

        // X-Organization-ID is set by APISIX forward-auth plugin.
        // Auth-service /session/resolve reads the org claim from the JWT
        // and returns it as a response header; APISIX injects it into the
        // upstream request via `upstream_headers`. The frontend MUST NOT
        // set this header from client-writable storage (localStorage/sessionStorage)
        // because XSS on any page could inject an arbitrary org ID.
        // See: Agion-UpCloud-GitOps/infrastructure/apisix/global-rules/global-forward-auth.yaml
      }
    ],
    beforeError: [
      ({ error }) => {
        // Auth redirects — side effects only. Return the original HTTPError
        // so ky's retry logic can inspect .response.status and respect the
        // statusCodes filter. Converting to ApiError here would bypass retry
        // filtering (ky treats non-HTTPError as a generic/network error).
        if (!(error instanceof HTTPError)) return error
        const { status } = error.response
        if (status === 401) {
          // Soft 401 via @agion/auth — confirms with /session before bouncing.
          // De-duped across concurrent calls; only redirects on confirmed-gone.
          void handleUnauthorized()
        } else if (status === 403) {
          const { pathname, search = '', hash = '' } = window.location
          if (pathname !== ACCESS_DENIED_PATH) {
            window.location.href = accessDeniedPath(`${pathname}${search}${hash}`)
          }
        }

        return error
      }
    ]
  }
})

// ============= Error Conversion =============
// Convert ky's HTTPError to our ApiError, extracting error codes from the
// response body. Called at the apiClient boundary so ky's internal retry
// logic (which relies on HTTPError.response.status) works correctly.

async function toApiError(error: unknown): Promise<never> {
  if (error instanceof HTTPError) {
    const response = error.response
    const errorData = error.data ?? response.statusText
    let errorCode: string | undefined
    if (typeof errorData === 'object' && errorData !== null) {
      const json = errorData as Record<string, unknown>
      errorCode = (typeof json['code'] === 'string' ? json['code'] : undefined)
        || (typeof json['error_code'] === 'string' ? json['error_code'] : undefined)
    }

    throw new ApiError(response.status, response.statusText, errorData, errorCode)
  }

  throw error
}

// ============= Base API Client =============
// Automatic case conversion:
// - Request data: camelCase -> snake_case (sent to backend)
// - Response data: snake_case -> camelCase (returned to frontend)

export const apiClient = {
  async get<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
    const response = await httpClient.get(path, { searchParams: params as Record<string, string> }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async post<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await httpClient.post(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await httpClient.put(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async patch<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await httpClient.patch(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async delete<T = void>(path: string): Promise<T> {
    try {
      const response = await httpClient.delete(path)
      if (response.status === 204) {
        return undefined as T
      }
      const json = await response.json()
      return keysToCamel<T>(json)
    } catch (error) {
      return toApiError(error)
    }
  },
}

// ============= Platform API Client =============
// Generic /v1/ prefix for cross-service endpoints (users, orgs) routed by APISIX.

const platformHttpClient: KyInstance = httpClient.extend({
  prefix: `${GATEWAY_URL}/v1`,
})

export const platformApiClient = {
  async get<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
    const response = await platformHttpClient.get(path, { searchParams: params as Record<string, string> }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async post<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await platformHttpClient.post(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await platformHttpClient.put(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async patch<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await platformHttpClient.patch(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async delete<T = void>(path: string): Promise<T> {
    try {
      const response = await platformHttpClient.delete(path)
      if (response.status === 204) {
        return undefined as T
      }
      const json = await response.json()
      return keysToCamel<T>(json)
    } catch (error) {
      return toApiError(error)
    }
  },
}

// ============= Observability API Client =============
// Observability is served by analytics-service, which can be exposed on a
// different host from the infra API gateway in deployed environments.

let observabilityHttpClient: KyInstance | undefined

const getObservabilityHttpClient = (): KyInstance => {
  observabilityHttpClient ??= httpClient.extend({
    prefix: `${getObservabilityGatewayUrl()}/v1/observability`,
  })
  return observabilityHttpClient
}

/** Per-call overrides for observability GETs. `retry: 0` sends exactly one request. */
export interface ObservabilityGetOptions {
  retry?: number
}

export const observabilityApiClient = {
  async get<T>(path: string, params?: Record<string, string | number | boolean>, options?: ObservabilityGetOptions): Promise<T> {
    const response = await getObservabilityHttpClient().get(path, {
      searchParams: params as Record<string, string>,
      ...(options?.retry !== undefined && { retry: options.retry }),
    }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async post<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await getObservabilityHttpClient().post(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await getObservabilityHttpClient().put(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async patch<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await getObservabilityHttpClient().patch(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async delete<T = void>(path: string): Promise<T> {
    try {
      const response = await getObservabilityHttpClient().delete(path)
      if (response.status === 204) {
        return undefined as T
      }
      const json = await response.json()
      return keysToCamel<T>(json)
    } catch (error) {
      return toApiError(error)
    }
  },
}

// ============= Governance API Client =============
// Same auth/case-conversion/error-handling as apiClient, different base path.

const governanceHttpClient: KyInstance = httpClient.extend({
  prefix: `${GATEWAY_URL}/v1/governance`,
})

export const governanceApiClient = {
  async get<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
    const response = await governanceHttpClient.get(path, { searchParams: params as Record<string, string> }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async post<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await governanceHttpClient.post(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await governanceHttpClient.put(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  // governance-service audited deletions (ingestion offering include) carry a
  // strict JSON body: reason, revision and confirmation travel with the request.
  async delete<T = void, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    try {
      const response = await governanceHttpClient.delete(path, { json: snakeCaseData })
      if (response.status === 204) {
        return undefined as T
      }
      const json = await response.json()
      return keysToCamel<T>(json)
    } catch (error) {
      return toApiError(error)
    }
  },
}

// ============= Auth API Client =============
// OIDC client management via auth-service admin endpoints.

const authHttpClient: KyInstance = httpClient.extend({
  prefix: `${GATEWAY_URL}/v1/auth/admin`,
})

export const authApiClient = {
  async get<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
    const response = await authHttpClient.get(path, { searchParams: params as Record<string, string> }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async post<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await authHttpClient.post(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async put<T, D = unknown>(path: string, data?: D): Promise<T> {
    const snakeCaseData = data ? keysToSnake(data) : undefined
    const response = await authHttpClient.put(path, { json: snakeCaseData }).json().catch(toApiError)
    return keysToCamel<T>(response)
  },

  async delete<T = void>(path: string): Promise<T> {
    try {
      const response = await authHttpClient.delete(path)
      if (response.status === 204) {
        return undefined as T
      }
      const json = await response.json()
      return keysToCamel<T>(json)
    } catch (error) {
      return toApiError(error)
    }
  },
}

// ============= Response Types =============

export interface ApiResponse<T> {
  data?: T
  message?: string
  success?: boolean
}
